// After an upgrade that unlocks new item types: the Watchlist prompt (new types now watched), the deep scan
// (LiveScan over only the new findings), then "See your map" — the map fills in with the new categories.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ITEM_TYPE_LABEL, PLAN_LABEL } from '../../domain/copy';
import type { Plan, WatchedItem } from '../../domain/types';
import { useData } from '../app/DataProvider';
import { Button } from '../components/Button';
import { Sheet } from '../components/Sheet';
import { Icon } from '../icons';
import { api, type FindingView } from '../lib/api';
import { LiveScan } from './LiveScan';
import s from './UpgradeTransition.module.css';

export function UpgradeTransition({ open, before, plan, onClose }: { open: boolean; before: string[]; plan: Plan; onClose: () => void }) {
  const { data } = useData();
  const navigate = useNavigate();
  const [state, setState] = useState<{ items: WatchedItem[]; findings: FindingView[]; score: number; scoreBefore: number } | null>(null);
  const [step, setStep] = useState<'items' | 'scan'>('items');

  useEffect(() => {
    if (!open) { setState(null); setStep('items'); return; }
    const scoreBefore = data?.session.score.value ?? 0;
    const itemsBefore = new Set(data?.watchlist.map((w) => w.id) ?? []);
    (async () => {
      const [f, w] = await Promise.all([api.findings(), api.watchlist()]);
      setState({ items: w.items.filter((i) => !itemsBefore.has(i.id)), findings: f.findings.filter((x) => !before.includes(x.id)), score: f.score.value, scoreBefore });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const finish = () => { onClose(); navigate('/'); };
  return (
    <Sheet open={open} onClose={onClose} title={`Welcome to ${PLAN_LABEL[plan]}`} subtitle="Your Watchlist grew and we ran a deep scan." testId="upgrade-transition">
      {!state ? <p className="bodySm">Setting up your plan…</p> : step === 'items' ? (
        <div className={s.body}>
          <p className="bodySmMedium">Now watching from your Nord Account</p>
          <ul className={s.items} data-testid="upgrade-items">
            {state.items.map((i) => (
              <li key={i.id} className={s.item}><Icon name="circle-check" size={16} className={s.ok} /><span className="bodySm"><strong>{ITEM_TYPE_LABEL[i.type].one}</strong> {i.label}</span></li>
            ))}
          </ul>
          <p className={`bodyXs ${s.muted}`}>You can add more types any time in your Watchlist.</p>
          <Button block onClick={() => setStep('scan')} data-testid="upgrade-run-scan">Run deep scan</Button>
        </div>
      ) : (
        <LiveScan title="Running your deep scan" sources={['Dark web', 'Transactions', 'Public profiles', 'Data brokers']} findings={state.findings}
          scoreBefore={state.scoreBefore} scoreAfter={state.score}
          footer={<Button block onClick={finish} data-testid="upgrade-see-map">See your map</Button>} />
      )}
    </Sheet>
  );
}
